/**
 * 국어의 로마자 표기법(Revised Romanization) 변환기.
 *
 * 네이버 지오코더는 `language=en` 을 줘도 한국어 주소만 돌려준다.
 * 영문 화면에서 주소를 한글로 노출할 수는 없으므로 여기서 직접 변환한다.
 *
 * 표기법의 자음 동화(유음화·비음화·연음·격음화)를 반영한다.
 * 된소리되기는 표기에 반영하지 않는다는 규정에 따라 적용하지 않는다.
 */

const ONSETS = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const NUCLEI = ['ㅏ','ㅐ','ㅑ','ㅒ','ㅓ','ㅔ','ㅕ','ㅖ','ㅗ','ㅘ','ㅙ','ㅚ','ㅛ','ㅜ','ㅝ','ㅞ','ㅟ','ㅠ','ㅡ','ㅢ','ㅣ'];
const CODAS = ['','ㄱ','ㄲ','ㄳ','ㄴ','ㄵ','ㄶ','ㄷ','ㄹ','ㄺ','ㄻ','ㄼ','ㄽ','ㄾ','ㄿ','ㅀ','ㅁ','ㅂ','ㅄ','ㅅ','ㅆ','ㅇ','ㅈ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];

// 겹받침은 연음될 때 뒷 자음만 다음 음절 초성으로 넘어간다.
const CLUSTERS = {
    'ㄳ': ['ㄱ','ㅅ'], 'ㄵ': ['ㄴ','ㅈ'], 'ㄶ': ['ㄴ','ㅎ'], 'ㄺ': ['ㄹ','ㄱ'],
    'ㄻ': ['ㄹ','ㅁ'], 'ㄼ': ['ㄹ','ㅂ'], 'ㄽ': ['ㄹ','ㅅ'], 'ㄾ': ['ㄹ','ㅌ'],
    'ㄿ': ['ㄹ','ㅍ'], 'ㅀ': ['ㄹ','ㅎ'], 'ㅄ': ['ㅂ','ㅅ'],
};

const ONSET_ROMAN = {
    'ㄱ':'g','ㄲ':'kk','ㄴ':'n','ㄷ':'d','ㄸ':'tt','ㄹ':'r','ㅁ':'m','ㅂ':'b','ㅃ':'pp',
    'ㅅ':'s','ㅆ':'ss','ㅇ':'','ㅈ':'j','ㅉ':'jj','ㅊ':'ch','ㅋ':'k','ㅌ':'t','ㅍ':'p','ㅎ':'h',
};

const CODA_ROMAN = {
    '':'','ㄱ':'k','ㄲ':'k','ㄳ':'k','ㄴ':'n','ㄵ':'n','ㄶ':'n','ㄷ':'t','ㄹ':'l','ㄺ':'k',
    'ㄻ':'m','ㄼ':'l','ㄽ':'l','ㄾ':'l','ㄿ':'p','ㅀ':'l','ㅁ':'m','ㅂ':'p','ㅄ':'p',
    'ㅅ':'t','ㅆ':'t','ㅇ':'ng','ㅈ':'t','ㅊ':'t','ㅋ':'k','ㅌ':'t','ㅍ':'p','ㅎ':'t',
};

const VOWEL_ROMAN = {
    'ㅏ':'a','ㅐ':'ae','ㅑ':'ya','ㅒ':'yae','ㅓ':'eo','ㅔ':'e','ㅕ':'yeo','ㅖ':'ye',
    'ㅗ':'o','ㅘ':'wa','ㅙ':'wae','ㅚ':'oe','ㅛ':'yo','ㅜ':'u','ㅝ':'wo','ㅞ':'we',
    'ㅟ':'wi','ㅠ':'yu','ㅡ':'eu','ㅢ':'ui','ㅣ':'i',
};

const isHangulSyllable = (ch) => ch >= '가' && ch <= '힣';

function decompose(ch) {
    const code = ch.charCodeAt(0) - 0xac00;
    return {
        onset: ONSETS[Math.floor(code / 588)],
        nucleus: NUCLEI[Math.floor((code % 588) / 28)],
        coda: CODAS[code % 28],
    };
}

// 받침이 ㄱ/ㅂ 계열인지 — 비음화 판단에 쓴다.
const K_CODAS = ['ㄱ','ㄲ','ㅋ','ㄳ','ㄺ'];
const T_CODAS = ['ㄷ','ㅌ','ㅅ','ㅆ','ㅈ','ㅊ','ㅎ'];
const P_CODAS = ['ㅂ','ㅍ','ㄼ','ㄿ','ㅄ'];
const ASPIRATE = { 'ㄱ':'ㅋ', 'ㄷ':'ㅌ', 'ㅈ':'ㅊ', 'ㅂ':'ㅍ' };

/** 인접한 두 음절 사이에 표기법이 정한 소리 변화를 적용한다. */
function applySandhi(syllables) {
    for (let i = 0; i < syllables.length - 1; i++) {
        const cur = syllables[i];
        const next = syllables[i + 1];
        if (!cur || !next) continue;
        const c = cur.coda;
        const o = next.onset;
        if (!c) continue;

        // 연음: 다음 음절이 모음으로 시작하면 받침이 그 초성으로 넘어간다. (관악 → 과낙 → Gwanak)
        if (o === 'ㅇ') {
            if (CLUSTERS[c]) {
                cur.coda = CLUSTERS[c][0];
                next.onset = CLUSTERS[c][1];
            } else if (c === 'ㅇ') {
                // 받침 ㅇ 은 넘어가지 않는다. (강아지 → Gangaji)
            } else if (c === 'ㅎ') {
                cur.coda = ''; // ㅎ 탈락 (좋아 → 조아)
            } else {
                cur.coda = '';
                next.onset = c;
            }
            continue;
        }

        // 격음화: 받침 ㅎ + ㄱ/ㄷ/ㅈ → 거센소리
        if (c === 'ㅎ' && ASPIRATE[o]) {
            cur.coda = '';
            next.onset = ASPIRATE[o];
            continue;
        }
        // 격음화: 받침 ㄱ/ㄷ/ㅂ/ㅈ + ㅎ → 거센소리
        if (o === 'ㅎ' && ASPIRATE[c]) {
            next.onset = ASPIRATE[c];
            cur.coda = '';
            continue;
        }

        // 유음화: ㄴ+ㄹ, ㄹ+ㄴ → ㄹㄹ (신림 → 실림 → Sillim)
        if (c === 'ㄴ' && o === 'ㄹ') { cur.coda = 'ㄹ'; next.onset = 'ㄹ'; continue; }
        if (c === 'ㄹ' && o === 'ㄴ') { next.onset = 'ㄹ'; continue; }

        // ㄹ 의 비음화: ㄱ·ㅇ·ㅁ·ㅂ 받침 + ㄹ → ㄴ (종로 → 종노 → Jongno)
        if (o === 'ㄹ' && (K_CODAS.includes(c) || c === 'ㅇ' || c === 'ㅁ' || P_CODAS.includes(c))) {
            next.onset = 'ㄴ';
            if (K_CODAS.includes(c)) cur.coda = 'ㅇ';      // 백로 → 뱅노
            else if (P_CODAS.includes(c)) cur.coda = 'ㅁ'; // 압력 → 암녁
            continue;
        }

        // 비음화: 파열음 받침 + ㄴ/ㅁ (국물 → 궁물)
        if (o === 'ㄴ' || o === 'ㅁ') {
            if (K_CODAS.includes(c)) { cur.coda = 'ㅇ'; continue; }
            if (T_CODAS.includes(c)) { cur.coda = 'ㄴ'; continue; }
            if (P_CODAS.includes(c)) { cur.coda = 'ㅁ'; continue; }
        }
    }
    return syllables;
}

/** 한글 문자열 하나를 로마자로. 한글이 아닌 문자는 그대로 둔다. */
export function romanizeWord(text) {
    if (!text) return '';
    const out = [];
    let buffer = [];

    const flush = () => {
        if (!buffer.length) return;
        applySandhi(buffer);
        out.push(buffer.map((s, i) => {
            // ㄹㄹ 은 'lr' 이 아니라 'll' 로 적는다. (신림 → Sillim)
            const prev = buffer[i - 1];
            const onset = (prev && prev.coda === 'ㄹ' && s.onset === 'ㄹ')
                ? 'l'
                : ONSET_ROMAN[s.onset];
            return onset + VOWEL_ROMAN[s.nucleus] + CODA_ROMAN[s.coda];
        }).join(''));
        buffer = [];
    };

    for (const ch of text) {
        if (isHangulSyllable(ch)) buffer.push(decompose(ch));
        else { flush(); out.push(ch); }
    }
    flush();
    return out.join('');
}

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** 첫 글자를 대문자로 한 로마자 표기. */
export function romanize(text) {
    return capitalize(romanizeWord(text));
}

export default romanize;
